import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Plus, FileText, X, Type, Settings, Phone, Mail, Sliders, CheckSquare, Upload, ChevronUp, ChevronDown, ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';
import BusinessLogoUpload from '../../components/BusinessLogoUpload';

interface LeadForm {
  id: string;
  business_id: string;
  title: string;
  description?: string;
  form_config?: {
    fields?: Array<{id: string, type: string, label: string, mandatory: boolean, isCustom?: boolean, options?: Array<{id: string, label: string}>, placeholder?: string}>;
    sub_header?: string;
    header_color?: string;
    logo_url?: string;
    background_color?: string;
    text_color?: string;
    button_color?: string;
    show_header?: boolean;
    show_business_name?: boolean;
    show_phone?: boolean;
    show_email?: boolean;
    show_address?: boolean;
    show_direct_contact?: boolean;
    redirect_url?: string;
    button_text?: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function LeadForms() {
  const { selectedBusiness } = useBusiness();
  const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<LeadForm | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newFormTitle, setNewFormTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formFields, setFormFields] = useState<Array<{id: string, type: string, label: string, mandatory: boolean, isCustom?: boolean, options?: Array<{id: string, label: string}>, placeholder?: string}>>([]);
  const [isFieldSettingsOpen, setIsFieldSettingsOpen] = useState(false);
  const [selectedFieldForSettings, setSelectedFieldForSettings] = useState<{id: string, type: string, label: string, mandatory: boolean, isCustom?: boolean, options?: Array<{id: string, label: string}>, placeholder?: string} | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [mobileView, setMobileView] = useState<'library' | 'editor'>('library');
  const [isFieldLibraryModalOpen, setIsFieldLibraryModalOpen] = useState(false);
  const [colorPickerModal, setColorPickerModal] = useState<{isOpen: boolean, type: 'header' | 'background' | 'text' | 'button' | null, currentColor: string}>({
    isOpen: false,
    type: null,
    currentColor: '#000000'
  });

  // Color picker modal functions
  const openColorPicker = (type: 'header' | 'background' | 'text' | 'button', currentColor: string) => {
    setColorPickerModal({
      isOpen: true,
      type,
      currentColor
    });
  };

  const closeColorPicker = () => {
    setColorPickerModal({
      isOpen: false,
      type: null,
      currentColor: '#000000'
    });
  };

  const handleColorSelect = (color: string) => {
    if (!selectedForm || !colorPickerModal.type) return;

    const colorProperty = `${colorPickerModal.type === 'button' ? 'button' : colorPickerModal.type}_color`;
    
    setSelectedForm(prev => prev ? {
      ...prev,
      form_config: {
        ...prev.form_config,
        [colorProperty]: color
      }
    } : null);
    setHasUnsavedChanges(true);
    closeColorPicker();
  };

  // Predefined color palette
  const colorPalette = [
    '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6', '#ffffff',
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
    '#ec4899', '#f43f5e', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d',
    '#16a34a', '#059669', '#0891b2', '#0284c7', '#2563eb', '#4f46e5', '#7c3aed',
    '#9333ea', '#c026d3', '#db2777', '#e11d48'
  ];

  // Fetch lead forms from database
  const fetchLeadForms = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('lead_forms')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeadForms(data || []);
    } catch (error) {
      console.error('Error fetching lead forms:', error);
    }
  };

  // Load lead forms when component mounts or business changes
  useEffect(() => {
    fetchLeadForms();
  }, [selectedBusiness?.id]);

  // Reset mobile view when no form is selected
  useEffect(() => {
    if (!selectedForm) {
      setMobileView('library');
    }
  }, [selectedForm]);

  const handleCreateForm = async () => {
    if (!newFormTitle.trim() || !selectedBusiness?.id || !supabase) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('lead_forms')
        .insert({
          business_id: selectedBusiness.id,
          title: newFormTitle.trim(),
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;

      setLeadForms(prev => [data, ...prev]);
      setSelectedForm(data);
      setMobileView('editor'); // Switch to editor view on mobile
      setNewFormTitle('');
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating lead form:', error);
      alert('Failed to create lead form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSelect = (form: LeadForm) => {
    setSelectedForm(form);
    setMobileView('editor'); // Switch to editor view on mobile
    // Load form fields from the database configuration
    if (form.form_config && form.form_config.fields) {
      setFormFields(form.form_config.fields);
    } else {
      setFormFields([]);
    }
    setHasUnsavedChanges(false);
  };

  // Helper function to generate clean field IDs from labels
  const generateFieldId = (label: string): string => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_')        // Replace spaces with underscores
      .replace(/_+/g, '_')         // Remove duplicate underscores
      .replace(/^_|_$/g, '');      // Remove leading/trailing underscores
  };

  const handleAddField = (fieldType: string, fieldLabel: string, isCustom: boolean = false, options?: Array<{id: string, label: string}>) => {
    const fieldId = generateFieldId(fieldLabel);
    
    const newField = {
      id: fieldId,
      type: fieldType,
      label: fieldLabel,
      mandatory: false,
      isCustom,
      options,
      placeholder: isCustom ? `Enter ${fieldLabel.toLowerCase()}...` : undefined
    };
    setFormFields(prev => [...prev, newField]);
    setHasUnsavedChanges(true);
    setIsFieldLibraryModalOpen(false); // Close modal on mobile after adding field
  };

  const handleOpenFieldSettings = (field: {id: string, type: string, label: string, mandatory: boolean, isCustom?: boolean, options?: Array<{id: string, label: string}>, placeholder?: string}) => {
    setSelectedFieldForSettings(field);
    setIsFieldSettingsOpen(true);
  };

  const handleUpdateField = (updatedField: {id: string, type: string, label: string, mandatory: boolean, isCustom?: boolean, options?: Array<{id: string, label: string}>, placeholder?: string}) => {
    setFormFields(prev => prev.map(field => 
      field.id === updatedField.id ? updatedField : field
    ));
    setIsFieldSettingsOpen(false);
    setSelectedFieldForSettings(null);
    setHasUnsavedChanges(true);
  };

  const handleMoveFieldUp = (fieldId: string) => {
    setFormFields(prev => {
      const currentIndex = prev.findIndex(field => field.id === fieldId);
      if (currentIndex <= 0) return prev; // Can't move up if it's the first item
      
      const newFields = [...prev];
      const temp = newFields[currentIndex];
      newFields[currentIndex] = newFields[currentIndex - 1];
      newFields[currentIndex - 1] = temp;
      
      setHasUnsavedChanges(true);
      return newFields;
    });
  };

  const handleMoveFieldDown = (fieldId: string) => {
    setFormFields(prev => {
      const currentIndex = prev.findIndex(field => field.id === fieldId);
      if (currentIndex < 0 || currentIndex >= prev.length - 1) return prev; // Can't move down if it's the last item
      
      const newFields = [...prev];
      const temp = newFields[currentIndex];
      newFields[currentIndex] = newFields[currentIndex + 1];
      newFields[currentIndex + 1] = temp;
      
      setHasUnsavedChanges(true);
      return newFields;
    });
  };



  const handleSaveForm = async () => {
    if (!selectedForm || !selectedBusiness?.id || !supabase) return;

    // Validate form title
    if (!selectedForm.title.trim()) {
      alert('Form title is required');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('lead_forms')
        .update({
          title: selectedForm.title,
          form_config: {
            fields: formFields,
            sub_header: selectedForm.form_config?.sub_header,
            header_color: selectedForm.form_config?.header_color,
            logo_url: selectedForm.form_config?.logo_url,
            background_color: selectedForm.form_config?.background_color,
            text_color: selectedForm.form_config?.text_color,
            button_color: selectedForm.form_config?.button_color,
            show_header: selectedForm.form_config?.show_header,
            show_business_name: selectedForm.form_config?.show_business_name,
            show_phone: selectedForm.form_config?.show_phone,
            show_email: selectedForm.form_config?.show_email,
            show_address: selectedForm.form_config?.show_address,
            show_direct_contact: selectedForm.form_config?.show_direct_contact,
            redirect_url: selectedForm.form_config?.redirect_url,
            button_text: selectedForm.form_config?.button_text,
            updated_at: new Date().toISOString()
          }
        })
        .eq('id', selectedForm.id)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      // Update the local form object
      setSelectedForm(prev => prev ? {
        ...prev,
        form_config: {
          fields: formFields,
          sub_header: selectedForm.form_config?.sub_header,
          header_color: selectedForm.form_config?.header_color,
          logo_url: selectedForm.form_config?.logo_url,
          background_color: selectedForm.form_config?.background_color,
          text_color: selectedForm.form_config?.text_color,
          button_color: selectedForm.form_config?.button_color,
          show_header: selectedForm.form_config?.show_header,
          show_business_name: selectedForm.form_config?.show_business_name,
          show_phone: selectedForm.form_config?.show_phone,
          show_email: selectedForm.form_config?.show_email,
          show_address: selectedForm.form_config?.show_address,
          show_direct_contact: selectedForm.form_config?.show_direct_contact,
          redirect_url: selectedForm.form_config?.redirect_url,
          button_text: selectedForm.form_config?.button_text,
          updated_at: new Date().toISOString()
        }
      } : null);

      // Update the forms list
      setLeadForms(prev => prev.map(form => 
        form.id === selectedForm.id ? {
          ...form,
          title: selectedForm.title,
          form_config: {
            fields: formFields,
            sub_header: selectedForm.form_config?.sub_header,
            header_color: selectedForm.form_config?.header_color,
            logo_url: selectedForm.form_config?.logo_url,
            background_color: selectedForm.form_config?.background_color,
            text_color: selectedForm.form_config?.text_color,
            button_color: selectedForm.form_config?.button_color,
            show_header: selectedForm.form_config?.show_header,
            show_business_name: selectedForm.form_config?.show_business_name,
            show_phone: selectedForm.form_config?.show_phone,
            show_email: selectedForm.form_config?.show_email,
            show_address: selectedForm.form_config?.show_address,
            show_direct_contact: selectedForm.form_config?.show_direct_contact,
            redirect_url: selectedForm.form_config?.redirect_url,
            button_text: selectedForm.form_config?.button_text,
            updated_at: new Date().toISOString()
          }
        } : form
      ));

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving form:', error);
      alert('Failed to save form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 relative z-0" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {/* Main Content */}
      <main className="w-full px-2 sm:px-4 lg:px-6 py-3 sm:py-8 lg:py-12 relative z-0">
        {/* Floating Container */}
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-3 sm:p-6 lg:p-8 mb-4 sm:mb-12 lg:mb-20 relative z-0">


          {/* Main Content Area - Mobile views vs Desktop side-by-side */}
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-6 lg:gap-8">
            {/* Library - Left Side (Hidden on mobile when in editor view) */}
            <div className={`lg:w-80 lg:flex-shrink-0 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 lg:p-6 shadow-lg border border-gray-200 order-2 lg:order-1 ${mobileView === 'editor' ? 'hidden lg:block' : ''}`}>
              <h4 className="text-base sm:text-xl font-bold text-gray-900 mb-3 sm:mb-6">Lead Forms Library</h4>
              
              {/* Create Form Button - Full width on mobile */}
              <button
                className="w-full inline-flex items-center justify-center px-3 sm:px-5 py-2 sm:py-4 text-sm sm:text-sm font-semibold text-white bg-purple-700 rounded-xl sm:rounded-2xl hover:bg-purple-600 transition-all duration-200 active:scale-95 mb-3 sm:mb-6 shadow-lg"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="w-4 h-4 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                <span className="sm:hidden">Create New Form</span>
                <span className="hidden sm:inline">Create Lead Form</span>
              </button>
              
              {leadForms.length === 0 ? (
                <div className="text-center py-6 sm:py-16 px-2 sm:px-4">
                  <div className="w-12 h-12 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-6 rounded-xl sm:rounded-2xl bg-gray-100 flex items-center justify-center shadow-sm border border-gray-200">
                    <FileText className="w-6 h-6 sm:w-10 sm:h-10 text-gray-400" />
                  </div>
                  <h5 className="text-sm sm:text-xl font-bold text-gray-900 mb-1 sm:mb-3">No lead forms created yet</h5>
                  <p className="text-xs sm:text-base text-gray-600 leading-relaxed">Click "Create New Form" to get started</p>
                  
                  {/* Mobile CTA */}
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="mt-3 sm:hidden w-full inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    Create Your First Form
                  </button>
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-4">
                  {leadForms.map((form) => (
                    <div
                      key={form.id}
                      onClick={() => handleFormSelect(form)}
                      className={`p-3 sm:p-5 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-200 active:scale-95 border shadow-sm ${
                        selectedForm?.id === form.id
                          ? 'bg-purple-50 border-purple-200 text-purple-900 shadow-purple-100'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-4">
                        <div className={`w-8 h-8 sm:w-8 sm:h-8 rounded-xl sm:rounded-full flex items-center justify-center shadow-sm border ${
                          selectedForm?.id === form.id 
                            ? 'bg-purple-100 border-purple-200' 
                            : 'bg-gray-100 border-gray-200'
                        }`}>
                          <FileText className={`w-4 h-4 sm:w-4 sm:h-4 ${
                            selectedForm?.id === form.id ? 'text-purple-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-sm font-bold truncate">{form.title}</p>
                          <p className="text-xs sm:text-xs text-gray-500 mt-0.5 sm:mt-0.5">
                            {form.form_config && form.form_config.fields && form.form_config.fields.length > 0 
                              ? `${form.form_config.fields.length} fields configured`
                              : 'No fields configured'
                            }
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5 sm:mt-0.5">
                            Created {new Date(form.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lead Form Editor - Right Side (Hidden on mobile when in library view) */}
            <div className={`flex-1 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 order-1 lg:order-2 ${mobileView === 'library' ? 'hidden lg:block' : ''}`}>
          {selectedForm ? (
            <div className="h-full flex flex-col">
              {/* Editor Header */}
              <div className="border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 rounded-t-xl sm:rounded-t-2xl bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <div className="flex items-center text-center sm:text-left">
                    {/* Mobile Back Button */}
                    <button
                      onClick={() => setMobileView('library')}
                      className="lg:hidden p-1.5 mr-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 active:scale-95"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    
                    <div className="flex-1">
                      <h4 className="text-base sm:text-xl font-bold text-gray-900">
                        {selectedForm.title}
                        {hasUnsavedChanges && (
                          <span className="text-xs sm:text-sm text-orange-600 ml-2 block sm:inline">(Unsaved changes)</span>
                        )}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">
                        Lead Form Editor
                      </p>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Compact mobile layout */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    {/* Preview and Copy Link - Side by side */}
                    <div className="flex space-x-2 sm:space-x-3">
                      <button 
                        onClick={() => {
                          if (selectedBusiness?.slug && selectedForm?.title) {
                            if (hasUnsavedChanges) {
                              alert('Please save your changes first before previewing the form.');
                              return;
                            }
                            const formSlug = selectedForm.title.toLowerCase().replace(/\s+/g, '-');
                            const publicUrl = `${window.location.origin}/${selectedBusiness.slug}/${formSlug}`;
                            window.open(publicUrl, '_blank');
                          }
                        }}
                        className="flex-1 sm:flex-none px-2.5 py-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg sm:rounded-md hover:bg-gray-200 transition-all duration-200 active:scale-95"
                      >
                        Preview
                      </button>
                      <button 
                        onClick={() => {
                          if (selectedBusiness?.slug && selectedForm?.title) {
                            const formSlug = selectedForm.title.toLowerCase().replace(/\s+/g, '-');
                            const publicUrl = `${window.location.origin}/${selectedBusiness.slug}/${formSlug}`;
                            navigator.clipboard.writeText(publicUrl);
                            alert('Form URL copied to clipboard!');
                          }
                        }}
                        className="flex-1 sm:flex-none px-2.5 py-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg sm:rounded-md hover:bg-gray-200 transition-all duration-200 active:scale-95"
                      >
                        Copy Link
                      </button>
                    </div>
                    
                    {/* Add Fields and Save - Side by side on mobile */}
                    <div className="flex space-x-2 sm:space-x-3">
                      {/* Add Fields Button - Mobile only */}
                      <button
                        onClick={() => setIsFieldLibraryModalOpen(true)}
                        className="lg:hidden flex-1 sm:flex-none inline-flex items-center justify-center px-2.5 py-1.5 text-xs font-semibold text-white bg-purple-700 rounded-lg hover:bg-purple-600 transition-all duration-200 active:scale-95"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Fields
                      </button>
                      
                      {/* Save Button */}
                      <button 
                        onClick={handleSaveForm}
                        disabled={isLoading || !hasUnsavedChanges}
                        className={`flex-1 sm:w-auto px-2.5 py-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-lg sm:rounded-md transition-all duration-200 active:scale-95 shadow-lg ${
                          hasUnsavedChanges && !isLoading
                            ? 'text-white bg-purple-700 hover:bg-purple-600' 
                            : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                        }`}
                      >
                        {isLoading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Editor Content - Stack on mobile */}
              <div className="flex-1 flex flex-col lg:flex-row">
                {/* Field Library - Hidden on mobile, shown on desktop */}
                <div className="hidden lg:block lg:w-64 lg:border-r border-gray-200 p-2 sm:p-5 lg:p-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-4">Field Library</h5>
                  </div>
                  
                  {/* Field Categories */}
                  <div className="space-y-3">
                                          <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Customer Information
                        </p>
                        <div className="space-y-1">
                        {/* Customer Info Component */}
                        <div 
                          className="p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95 shadow-sm"
                          onClick={() => handleAddField('customer_info', 'Customer Information')}
                        >
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-gray-500" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Customer Info</p>
                              <p className="text-xs text-gray-500">Creates Customer Record</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Basic Fields
                      </p>
                      <div className="space-y-1">
                        {/* Checkbox Field */}
                        <div 
                          className="p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95 shadow-sm"
                          onClick={() => handleAddField('checkbox', 'Select Options', false, [
                            {id: generateFieldId('option_1'), label: 'Option 1'},
                            {id: generateFieldId('option_2'), label: 'Option 2'}
                          ])}
                        >
                          <div className="flex items-center space-x-2">
                            <CheckSquare className="w-4 h-4 text-gray-500" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Checkbox</p>
                              <p className="text-xs text-gray-500">Multiple choice options</p>
                            </div>
                          </div>
                        </div>

                        {/* Custom Field */}
                        <div 
                          className="p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95 shadow-sm"
                          onClick={() => handleAddField('text', 'Custom Field', true)}
                        >
                          <div className="flex items-center space-x-2">
                            <Sliders className="w-4 h-4 text-gray-500" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Custom Field</p>
                              <p className="text-xs text-gray-500">Configurable field type</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Canvas - Main Area */}
                <div className="flex-1 p-2 sm:p-5 lg:p-6">
                  <div className="space-y-3 sm:space-y-6">
                    {/* Form Info - Hidden on mobile */}
                    <div className="hidden sm:block bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-sm border border-gray-200">
                      <h5 className="text-sm sm:text-sm font-bold sm:font-medium text-gray-900 mb-2 sm:mb-2">
                        {selectedForm.title}
                      </h5>
                      <p className="text-xs sm:text-xs text-gray-500 mb-2 sm:mb-2 leading-relaxed">
                        Add fields from the library to build your lead capture form
                      </p>
                      {selectedBusiness?.slug && selectedForm?.title && (
                        <div className="text-xs sm:text-xs">
                          <span className="text-gray-500 font-semibold">Public URL: </span>
                          <div className="mt-1.5 sm:mt-1">
                            <code className="bg-white px-2 py-1.5 sm:px-2 sm:py-1 rounded-lg sm:rounded text-purple-600 text-xs break-all shadow-sm border border-gray-200">
                              {window.location.origin}/{selectedBusiness.slug}/{selectedForm.title.toLowerCase().replace(/\s+/g, '-')}
                            </code>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Form Builder Canvas */}
                    <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl sm:rounded-2xl min-h-[250px] sm:min-h-[400px] p-3 sm:p-6 shadow-sm">
                      {formFields.length === 0 ? (
                        <div className="text-center py-8 sm:py-16 px-2 sm:px-4">
                          <div className="w-12 h-12 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-6 rounded-xl sm:rounded-2xl bg-gray-100 flex items-center justify-center shadow-sm border border-gray-200">
                            <FileText className="w-6 h-6 sm:w-10 sm:h-10 text-gray-400" />
                          </div>
                          <h6 className="text-base sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-3">
                            Your form is empty
                          </h6>
                          <p className="text-xs sm:text-base text-gray-600 mb-3 sm:mb-6 leading-relaxed">
                            Add fields from the library to start building your lead form
                          </p>
                          <div className="text-xs sm:text-xs text-gray-500 bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-3 max-w-sm mx-auto shadow-sm border border-gray-200">
                            <p className="font-semibold mb-1.5 sm:mb-1 text-gray-700">💡 How it works:</p>
                            <div className="space-y-0.5 sm:space-y-1 text-left">
                              <p>• Select preset fields from the library</p>
                              <p>• Click to add them to your form</p>
                              <p>• Customize each field as needed</p>
                            </div>
                          </div>
                          
                          {/* Mobile help text for field library */}
                          <button
                            className="mt-4 sm:hidden w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-purple-700 bg-purple-50 rounded-xl hover:bg-purple-100 transition-all duration-200 active:scale-95 shadow-lg border border-purple-200"
                            onClick={() => setIsFieldLibraryModalOpen(true)}
                          >
                            <Plus className="w-4 h-4 mr-1.5" />
                            Add Fields
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2.5 sm:space-y-4">
                          {formFields.map((field, index) => (
                            <div key={field.id} className="bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-sm relative">
                              {/* Action Buttons - Top Right Corner Stack */}
                              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col space-y-1 z-10">
                                {/* Delete Button */}
                                <button
                                  onClick={() => {
                                    setFormFields(prev => prev.filter(f => f.id !== field.id));
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 active:scale-95"
                                  title="Remove Field"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                                
                                {/* Settings Button */}
                                <button
                                  onClick={() => handleOpenFieldSettings(field)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 active:scale-95"
                                  title="Field Settings"
                                >
                                  <Settings className="w-4 h-4" />
                                </button>
                              </div>

                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 mb-2 sm:mb-2 pr-8 sm:pr-10">
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
                                  <label className="text-sm sm:text-sm font-bold sm:font-medium text-gray-700">{field.label}</label>
                                  {field.mandatory && (
                                    <span className="text-red-500 text-sm sm:text-sm">*</span>
                                  )}
                                  <span className="hidden sm:inline text-xs px-2 py-0.5 sm:px-2 sm:py-0.5 bg-gray-200 text-gray-600 rounded-full font-semibold">
                                    {field.mandatory ? 'Required' : 'Optional'}
                                  </span>

                                  {field.isCustom && field.type !== 'customer_info' && (
                                    <span className="hidden sm:inline text-xs px-2 py-0.5 sm:px-2 sm:py-0.5 bg-purple-100 text-purple-600 rounded-full font-semibold">
                                      Custom ({field.type})
                                    </span>
                                  )}
                                  {field.type === 'checkbox' && (
                                    <span className="hidden sm:inline text-xs px-2 py-0.5 sm:px-2 sm:py-0.5 bg-green-100 text-green-600 rounded-full font-semibold">
                                      Checkbox ({field.options?.length || 0} options)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-center sm:justify-end space-x-1.5 sm:space-x-1">
                                  {/* Move Up Button */}
                                  <button
                                    onClick={() => handleMoveFieldUp(field.id)}
                                    disabled={index === 0}
                                    className={`p-1.5 sm:p-1 rounded-lg sm:rounded-md transition-all duration-200 active:scale-95 ${
                                      index === 0 
                                        ? 'text-gray-300 cursor-not-allowed bg-gray-50 sm:bg-transparent' 
                                        : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50 sm:hover:bg-transparent'
                                    }`}
                                    title="Move Up"
                                  >
                                    <ChevronUp className="w-4 h-4 sm:w-4 sm:h-4" />
                                  </button>
                                  
                                  {/* Move Down Button */}
                                  <button
                                    onClick={() => handleMoveFieldDown(field.id)}
                                    disabled={index === formFields.length - 1}
                                    className={`p-1.5 sm:p-1 rounded-lg sm:rounded-md transition-all duration-200 active:scale-95 ${
                                      index === formFields.length - 1 
                                        ? 'text-gray-300 cursor-not-allowed bg-gray-50 sm:bg-transparent' 
                                        : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50 sm:hover:bg-transparent'
                                    }`}
                                    title="Move Down"
                                  >
                                    <ChevronDown className="w-4 h-4 sm:w-4 sm:h-4" />
                                  </button>
                                </div>
                              </div>
                              {field.type === 'customer_info' ? (
                                <div className="space-y-2.5 sm:space-y-3 p-3 sm:p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg shadow-sm">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                                    <div>
                                      <label className="block text-xs sm:text-xs font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-1">First Name *</label>
                                      <input
                                        type="text"
                                        className="w-full px-3 py-2 sm:px-2 sm:py-1.5 border border-gray-300 rounded-lg sm:rounded text-sm sm:text-sm bg-white shadow-sm"
                                        placeholder="Enter first name..."
                                        disabled
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs sm:text-xs font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-1">Last Name *</label>
                                      <input
                                        type="text"
                                        className="w-full px-3 py-2 sm:px-2 sm:py-1.5 border border-gray-300 rounded-lg sm:rounded text-sm sm:text-sm bg-white shadow-sm"
                                        placeholder="Enter last name..."
                                        disabled
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs sm:text-xs font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-1">Email Address *</label>
                                    <input
                                      type="email"
                                      className="w-full px-3 py-2 sm:px-2 sm:py-1.5 border border-gray-300 rounded-lg sm:rounded text-sm sm:text-sm bg-white shadow-sm"
                                      placeholder="Enter email address..."
                                      disabled
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs sm:text-xs font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-1">Phone Number *</label>
                                    <input
                                      type="tel"
                                      className="w-full px-3 py-2 sm:px-2 sm:py-1.5 border border-gray-300 rounded-lg sm:rounded text-sm sm:text-sm bg-white shadow-sm"
                                      placeholder="Enter phone number..."
                                      disabled
                                    />
                                  </div>
                                  <p className="text-xs sm:text-xs text-gray-600 bg-purple-50 p-2 sm:p-2 rounded-lg sm:rounded border border-purple-200">
                                    This component automatically creates a customer record when the form is submitted.
                                  </p>
                                </div>
                              ) : field.type === 'checkbox' ? (
                                <div className="space-y-2 sm:space-y-2 p-3 sm:p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg shadow-sm">
                                  {field.options?.map((option) => (
                                    <div key={option.id} className="flex items-center space-x-2.5 sm:space-x-2">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 sm:w-4 sm:h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        disabled
                                      />
                                      <label className="text-sm sm:text-sm text-gray-700 font-medium">{option.label}</label>
                                    </div>
                                  )) || (
                                    <p className="text-sm sm:text-sm text-gray-500 font-medium">No options configured</p>
                                  )}
                                </div>
                              ) : field.type === 'textarea' ? (
                                <div className="p-3 sm:p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg shadow-sm">
                                  <textarea
                                    className="w-full px-3 py-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md bg-white h-20 sm:h-20 resize-none text-sm sm:text-sm shadow-sm"
                                    placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                                    disabled
                                  />
                                </div>
                              ) : (
                                <div className="p-3 sm:p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg shadow-sm">
                                  <input
                                    type={field.type}
                                    className="w-full px-3 py-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md bg-white text-sm sm:text-sm shadow-sm"
                                    placeholder={
                                      field.placeholder || (
                                        field.type === 'email' ? 'Enter email address...' :
                                        field.type === 'tel' ? 'Enter phone number...' :
                                        field.type === 'number' ? 'Enter number...' :
                                        field.type === 'url' ? 'Enter URL...' :
                                        field.type === 'date' ? 'Select date...' :
                                        `Enter ${field.label.toLowerCase()}...`
                                      )
                                    }
                                    disabled
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Form Settings - Collapsed by default */}
                    <details className="border border-gray-200 rounded-lg sm:rounded-2xl shadow-sm">
                      <summary className="px-3 sm:px-5 py-2.5 sm:py-3 bg-gray-50 cursor-pointer text-xs sm:text-sm font-bold sm:font-medium text-gray-900 rounded-t-lg sm:rounded-t-2xl hover:bg-gray-100 transition-all duration-200 active:scale-[0.99]">
                        Form Settings & Styling
                      </summary>
                      <div className="p-2.5 sm:p-5 space-y-3 sm:space-y-4 bg-white rounded-b-lg sm:rounded-b-2xl">
                        {/* Basic Settings */}
                        <div className="space-y-3 sm:space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div>
                              <label className="block text-xs sm:text-sm font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-2">
                                Form Title
                              </label>
                              <input
                                type="text"
                                value={selectedForm.title}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    title: e.target.value
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="w-full px-2.5 py-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-xs sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                placeholder="Enter form title"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-2">
                                Status
                              </label>
                              <select className="w-full px-2.5 py-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-xs sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm">
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Sub Header */}
                          <div>
                            <label className="block text-xs sm:text-sm font-semibold sm:font-medium text-gray-700 mb-1.5 sm:mb-2">
                              Sub Header
                            </label>
                            <input
                              type="text"
                              value={selectedForm.form_config?.sub_header || ''}
                              onChange={(e) => {
                                setSelectedForm(prev => prev ? {
                                  ...prev,
                                  form_config: {
                                    ...prev.form_config,
                                    sub_header: e.target.value
                                  }
                                } : null);
                                setHasUnsavedChanges(true);
                              }}
                              placeholder="e.g., Please fill out the form below"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Optional text that appears below the form title
                            </p>
                          </div>

                          {/* Redirect URL */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Redirect URL (Optional)
                            </label>
                            <input
                              type="url"
                              value={selectedForm.form_config?.redirect_url || ''}
                              onChange={(e) => {
                                setSelectedForm(prev => prev ? {
                                  ...prev,
                                  form_config: {
                                    ...prev.form_config,
                                    redirect_url: e.target.value
                                  }
                                } : null);
                                setHasUnsavedChanges(true);
                              }}
                              placeholder="https://your-website.com/thank-you"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              If provided, users will be redirected to this URL after form submission instead of showing the thank you page
                            </p>
                          </div>

                          {/* Submit Button Text */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Submit Button Text
                            </label>
                            <input
                              type="text"
                              value={selectedForm.form_config?.button_text || ''}
                              onChange={(e) => {
                                setSelectedForm(prev => prev ? {
                                  ...prev,
                                  form_config: {
                                    ...prev.form_config,
                                    button_text: e.target.value
                                  }
                                } : null);
                                setHasUnsavedChanges(true);
                              }}
                              placeholder="Submit Form"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Customize the text displayed on the submit button (e.g., "Get Quote", "Book Now", "Send Message")
                            </p>
                          </div>
                        </div>

                        {/* Header Styling */}
                        <div>
                          <p className="text-sm font-medium text-gray-900 mb-3">Header Styling</p>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Header Color
                              </label>
                              <div className="flex items-center space-x-2">
                                {/* Mobile: Color button that opens modal */}
                                <button
                                  onClick={() => openColorPicker('header', selectedForm.form_config?.header_color || '#000000')}
                                  className="sm:hidden w-8 h-8 rounded border border-gray-300 flex items-center justify-center"
                                  style={{ backgroundColor: selectedForm.form_config?.header_color || '#000000' }}
                                >
                                  <span className="sr-only">Select header color</span>
                                </button>
                                
                                {/* Desktop: Native color picker */}
                                <input
                                  type="color"
                                  value={selectedForm.form_config?.header_color || '#000000'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        header_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:block w-6 h-6 rounded border border-gray-300"
                                />
                                <input
                                  type="text"
                                  value={selectedForm.form_config?.header_color || '#000000'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        header_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:flex flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="#000000"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Business Logo
                              </label>
                              <BusinessLogoUpload size="small" />
                              <p className="text-xs text-gray-500 mt-1">
                                This logo will appear on all your lead forms. Changes made here will also update your business logo across the platform.
                              </p>
                            </div>
                          </div>
                          <div className="mb-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedForm.form_config?.show_header !== false}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    form_config: {
                                      ...prev.form_config,
                                      show_header: e.target.checked
                                    }
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Show header
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">
                              Display the header section with logo and business name
                            </p>
                          </div>
                          <div className="mb-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedForm.form_config?.show_business_name !== false}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    form_config: {
                                      ...prev.form_config,
                                      show_business_name: e.target.checked
                                    }
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Display business name in header
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">
                              Show your business name next to the logo in the form header
                            </p>
                          </div>
                        </div>

                        {/* Display Options */}
                        <div>
                          <p className="text-sm font-medium text-gray-900 mb-3">Display Options</p>
                          <div className="space-y-3">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedForm.form_config?.show_phone || false}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    form_config: {
                                      ...prev.form_config,
                                      show_phone: e.target.checked
                                    }
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Display phone number
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedForm.form_config?.show_email || false}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    form_config: {
                                      ...prev.form_config,
                                      show_email: e.target.checked
                                    }
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Display email address
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedForm.form_config?.show_address || false}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    form_config: {
                                      ...prev.form_config,
                                      show_address: e.target.checked
                                    }
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Display location address
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedForm.form_config?.show_direct_contact || false}
                                onChange={(e) => {
                                  setSelectedForm(prev => prev ? {
                                    ...prev,
                                    form_config: {
                                      ...prev.form_config,
                                      show_direct_contact: e.target.checked
                                    }
                                  } : null);
                                  setHasUnsavedChanges(true);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Display direct contact module
                              </span>
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Show contact information on the right side of the form
                          </p>
                        </div>

                        {/* Form Styling */}
                        <div>
                          <p className="text-sm font-medium text-gray-900 mb-3">Form Styling</p>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Background
                              </label>
                              <div className="flex items-center space-x-2">
                                {/* Mobile: Color button that opens modal */}
                                <button
                                  onClick={() => openColorPicker('background', selectedForm.form_config?.background_color || '#ffffff')}
                                  className="sm:hidden w-8 h-8 rounded border border-gray-300 flex items-center justify-center"
                                  style={{ backgroundColor: selectedForm.form_config?.background_color || '#ffffff' }}
                                >
                                  <span className="sr-only">Select background color</span>
                                </button>
                                
                                {/* Desktop: Native color picker */}
                                <input
                                  type="color"
                                  value={selectedForm.form_config?.background_color || '#ffffff'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        background_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:block w-6 h-6 rounded border border-gray-300"
                                />
                                <input
                                  type="text"
                                  value={selectedForm.form_config?.background_color || '#ffffff'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        background_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:flex flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Text Color
                              </label>
                              <div className="flex items-center space-x-2">
                                {/* Mobile: Color button that opens modal */}
                                <button
                                  onClick={() => openColorPicker('text', selectedForm.form_config?.text_color || '#000000')}
                                  className="sm:hidden w-8 h-8 rounded border border-gray-300 flex items-center justify-center"
                                  style={{ backgroundColor: selectedForm.form_config?.text_color || '#000000' }}
                                >
                                  <span className="sr-only">Select text color</span>
                                </button>
                                
                                {/* Desktop: Native color picker */}
                                <input
                                  type="color"
                                  value={selectedForm.form_config?.text_color || '#000000'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        text_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:block w-6 h-6 rounded border border-gray-300"
                                />
                                <input
                                  type="text"
                                  value={selectedForm.form_config?.text_color || '#000000'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        text_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:flex flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="#000000"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Button Color
                              </label>
                              <div className="flex items-center space-x-2">
                                {/* Mobile: Color button that opens modal */}
                                <button
                                  onClick={() => openColorPicker('button', selectedForm.form_config?.button_color || '#3B82F6')}
                                  className="sm:hidden w-8 h-8 rounded border border-gray-300 flex items-center justify-center"
                                  style={{ backgroundColor: selectedForm.form_config?.button_color || '#3B82F6' }}
                                >
                                  <span className="sr-only">Select button color</span>
                                </button>
                                
                                {/* Desktop: Native color picker */}
                                <input
                                  type="color"
                                  value={selectedForm.form_config?.button_color || '#3B82F6'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        button_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:block w-6 h-6 rounded border border-gray-300"
                                />
                                <input
                                  type="text"
                                  value={selectedForm.form_config?.button_color || '#3B82F6'}
                                  onChange={(e) => {
                                    setSelectedForm(prev => prev ? {
                                      ...prev,
                                      form_config: {
                                        ...prev.form_config,
                                        button_color: e.target.value
                                      }
                                    } : null);
                                    setHasUnsavedChanges(true);
                                  }}
                                  className="hidden sm:flex flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="#3B82F6"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>
                        ) : (
                <div className="h-full flex items-center justify-center p-4 sm:p-8">
                  <div className="text-center py-8 sm:py-16 px-2 sm:px-4">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-8 rounded-xl sm:rounded-2xl bg-gray-100 flex items-center justify-center shadow-sm border border-gray-200">
                      <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                    </div>
                    <h5 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">No lead form selected</h5>
                    <p className="text-sm sm:text-lg text-gray-600 leading-relaxed max-w-sm mx-auto">
                      {leadForms.length === 0
                        ? "Create your first lead form to get started"
                        : "Select a lead form from the library to edit it"
                      }
                    </p>
                    
                    {/* Mobile navigation hint */}
                    <button
                      onClick={() => setMobileView('library')}
                      className="mt-4 lg:hidden w-full max-w-xs mx-auto inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-purple-700 bg-purple-50 rounded-xl hover:bg-purple-100 transition-all duration-200 active:scale-95 shadow-lg border border-purple-200"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1.5" />
                      Back to Library
                    </button>
                    
                    {/* Desktop CTA for creating form */}
                    {leadForms.length === 0 && (
                      <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="mt-4 hidden lg:inline-flex w-full max-w-xs mx-auto items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg"
                      >
                        <Plus className="w-4 h-4 mr-1.5" />
                        Create First Form
                      </button>
                    )}
                  </div>
                </div>
              )}
                    </div>
          </div>
        </div>
      </main>

      {/* Mobile Field Library Modal */}
      {isFieldLibraryModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200 max-h-[80vh] sm:max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900">Add Field</h3>
              <button 
                onClick={() => setIsFieldLibraryModalOpen(false)} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Customer Information
                  </p>
                  <div className="space-y-2">
                    {/* Customer Info Component */}
                    <div 
                      className="p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95 shadow-sm"
                      onClick={() => handleAddField('customer_info', 'Customer Information')}
                    >
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-base font-semibold text-gray-900">Customer Info</p>
                          <p className="text-sm text-gray-500">Creates Customer Record</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Basic Fields
                  </p>
                  <div className="space-y-2">
                    {/* Checkbox Field */}
                    <div 
                      className="p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95 shadow-sm"
                      onClick={() => handleAddField('checkbox', 'Select Options', false, [
                        {id: generateFieldId('option_1'), label: 'Option 1'},
                        {id: generateFieldId('option_2'), label: 'Option 2'}
                      ])}
                    >
                      <div className="flex items-center space-x-3">
                        <CheckSquare className="w-5 h-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-base font-semibold text-gray-900">Checkbox</p>
                          <p className="text-sm text-gray-500">Multiple choice options</p>
                        </div>
                      </div>
                    </div>

                    {/* Custom Field */}
                    <div 
                      className="p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95 shadow-sm"
                      onClick={() => handleAddField('text', 'Custom Field', true)}
                    >
                      <div className="flex items-center space-x-3">
                        <Sliders className="w-5 h-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-base font-semibold text-gray-900">Custom Field</p>
                          <p className="text-sm text-gray-500">Configurable field type</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Field Settings Modal */}
      {isFieldSettingsOpen && selectedFieldForSettings && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200 max-h-[90vh] sm:max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900">Field Settings</h3>
              <button 
                onClick={() => setIsFieldSettingsOpen(false)} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {selectedFieldForSettings.label} Field
                  {selectedFieldForSettings.isCustom && (
                    <span className="text-xs font-normal text-blue-600 ml-2">(Custom)</span>
                  )}
                </h4>
                <p className="text-xs text-gray-500">
                  {selectedFieldForSettings.isCustom 
                    ? "Configure the type, label and settings for this custom field"
                    : "Configure the settings for this field"
                  }
                </p>
              </div>

              <div className="space-y-4">
                {/* Field Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field Label
                  </label>
                  <input
                    type="text"
                    value={selectedFieldForSettings.label}
                    onChange={(e) => setSelectedFieldForSettings(prev => 
                      prev ? {...prev, label: e.target.value} : null
                    )}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Placeholder Text - Only for custom fields and non-checkbox types */}
                {selectedFieldForSettings.isCustom && selectedFieldForSettings.type !== 'checkbox' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Placeholder Text
                    </label>
                    <input
                      type="text"
                      value={selectedFieldForSettings.placeholder || ''}
                      onChange={(e) => setSelectedFieldForSettings(prev => 
                        prev ? {...prev, placeholder: e.target.value} : null
                      )}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter example text for this field..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This text appears inside the field as a hint for users (e.g., "Enter vehicle type i.e. 2024 Tesla Model 3")
                    </p>
                  </div>
                )}

                {/* Field Type - Only for custom fields */}
                {selectedFieldForSettings.isCustom && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Field Type
                    </label>
                    <select
                      value={selectedFieldForSettings.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setSelectedFieldForSettings(prev => {
                          if (!prev) return null;
                          
                          // If changing to checkbox and no options exist, add default options
                          if (newType === 'checkbox' && (!prev.options || prev.options.length === 0)) {
                            return {
                              ...prev, 
                              type: newType,
                              placeholder: undefined, // Clear placeholder for checkbox
                              options: [
                                {id: generateFieldId('option_1'), label: 'Option 1'},
                                {id: generateFieldId('option_2'), label: 'Option 2'}
                              ]
                            };
                          }
                          
                          // If changing from checkbox to other type, add default placeholder if none exists
                          if (prev.type === 'checkbox' && newType !== 'checkbox' && !prev.placeholder) {
                            return {
                              ...prev, 
                              type: newType,
                              placeholder: `Enter ${prev.label.toLowerCase()}...`
                            };
                          }
                          
                          return {...prev, type: newType};
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="tel">Phone</option>
                      <option value="url">URL</option>
                      <option value="date">Date</option>
                      <option value="textarea">Textarea</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose the input type for this field
                    </p>
                  </div>
                                 )}

                {/* Checkbox Options - Only for checkbox fields */}
                {selectedFieldForSettings.type === 'checkbox' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Checkbox Options
                    </label>
                    <div className="space-y-2 mb-3">
                      {selectedFieldForSettings.options?.map((option, index) => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={option.label}
                            onChange={(e) => {
                              const newOptions = [...(selectedFieldForSettings.options || [])];
                              newOptions[index] = { ...option, label: e.target.value };
                              setSelectedFieldForSettings(prev => 
                                prev ? {...prev, options: newOptions} : null
                              );
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Option label"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newOptions = (selectedFieldForSettings.options || []).filter(opt => opt.id !== option.id);
                              setSelectedFieldForSettings(prev => 
                                prev ? {...prev, options: newOptions} : null
                              );
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const optionNumber = (selectedFieldForSettings.options?.length || 0) + 1;
                        const newOption = {
                          id: generateFieldId(`option_${optionNumber}`),
                          label: `Option ${optionNumber}`
                        };
                        setSelectedFieldForSettings(prev => 
                          prev ? {...prev, options: [...(prev.options || []), newOption]} : null
                        );
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Option
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Users can select multiple options from this list
                    </p>
                  </div>
                )}

                {/* Mandatory Toggle */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedFieldForSettings.mandatory}
                      onChange={(e) => setSelectedFieldForSettings(prev => 
                        prev ? {...prev, mandatory: e.target.checked} : null
                      )}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      This field is mandatory to fill out
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedFieldForSettings.mandatory 
                      ? "Users must fill out this field before submitting the form"
                      : "This field is optional for users to fill out"
                    }
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t border-gray-200 mt-6 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setIsFieldSettingsOpen(false)}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl sm:rounded-md hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => selectedFieldForSettings && handleUpdateField(selectedFieldForSettings)}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-semibold text-white bg-purple-700 rounded-xl sm:rounded-md hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Lead Form Modal */}
      {isCreateModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900">Create New Lead Form</h3>
              <button 
                onClick={() => setIsCreateModalOpen(false)} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateForm();
              }}
              className="px-3 sm:px-6 py-4 sm:py-4"
            >
              <div className="mb-4 sm:mb-4">
                <label className="block text-sm sm:text-sm font-semibold sm:font-medium text-gray-700 mb-2 sm:mb-2">
                  Form Title
                </label>
                <input
                  type="text"
                  value={newFormTitle}
                  onChange={(e) => setNewFormTitle(e.target.value)}
                  className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-sm sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                  placeholder="Enter lead form title"
                  required
                  autoFocus
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="w-full sm:w-auto px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg sm:rounded-md hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newFormTitle.trim() || isLoading}
                  className="w-full sm:w-auto px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-white bg-purple-700 rounded-lg sm:rounded-md hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Form'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
              )}

      {/* Color Picker Modal */}
      {colorPickerModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={closeColorPicker}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Choose {colorPickerModal.type} Color
                </h3>
                <button
                  onClick={closeColorPicker}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-7 gap-2 mb-4">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      colorPickerModal.currentColor === color 
                        ? 'border-blue-500 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {colorPickerModal.currentColor === color && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full shadow-sm"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={colorPickerModal.currentColor}
                    onChange={(e) => setColorPickerModal(prev => ({
                      ...prev,
                      currentColor: e.target.value
                    }))}
                    className="w-10 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={colorPickerModal.currentColor}
                    onChange={(e) => setColorPickerModal(prev => ({
                      ...prev,
                      currentColor: e.target.value
                    }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="#000000"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={closeColorPicker}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleColorSelect(colorPickerModal.currentColor)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
} 